import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AdminEnvironmentPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [changing, setChanging] = useState(false);

  // Load environment config
  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/environment');
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load environment config:', error);
      alert('Failed to load environment config: ' + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadConfig();
  }, [isAdmin]);

  // Change mode
  const changeMode = async (newMode) => {
    // Smart suggestions for location based on mode
    let suggestedLocation = null;
    let locationMessage = '';
    
    if (newMode === 'production' && config.location === 'local') {
      suggestedLocation = 'server';
      locationMessage = '\n\n💡 Tip: Production mode typically runs on Server location. Would you like to switch to Server as well?';
    } else if (newMode === 'development' && config.location === 'server') {
      suggestedLocation = 'local';
      locationMessage = '\n\n💡 Tip: Development mode typically runs on Local location. Would you like to switch to Local as well?';
    }
    
    if (!window.confirm(`Switch to ${newMode.toUpperCase()} mode?${locationMessage}`)) return;

    setChanging(true);
    try {
      // Change mode
      await axios.post('/environment/mode', { mode: newMode });
      
      // If user agreed to suggestion, also change location
      if (suggestedLocation && window.confirm(`Switch location to ${suggestedLocation.toUpperCase()}?`)) {
        await axios.post('/environment/location', { location: suggestedLocation });
        alert(`Successfully switched to ${newMode.toUpperCase()} mode and ${suggestedLocation.toUpperCase()} location!`);
      } else {
        alert(`Successfully switched to ${newMode.toUpperCase()} mode!`);
      }
      
      loadConfig();
    } catch (error) {
      console.error('Failed to change mode:', error);
      alert('Failed to change mode: ' + error.message);
    }
    setChanging(false);
  };

  // Change location
  const changeLocation = async (newLocation) => {
    if (!window.confirm(`Switch to ${newLocation.toUpperCase()} location?`)) return;

    setChanging(true);
    try {
      await axios.post('/environment/location', { location: newLocation });
      alert(`Successfully switched to ${newLocation.toUpperCase()} location!`);
      loadConfig();
    } catch (error) {
      console.error('Failed to change location:', error);
      alert('Failed to change location: ' + error.message);
    }
    setChanging(false);
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.5rem'
        }}>
          🌍 Environment & System Controls
        </h1>
        <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
          Manage system configuration and deployment settings
        </p>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
          Loading environment configuration...
        </div>
      ) : config ? (
        <>
          {/* Current Status Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {/* Mode Status */}
            <div style={{
              background: config.mode === 'development'
                ? 'rgba(34, 197, 94, 0.1)'
                : 'rgba(194, 85, 92, 0.1)',
              border: `1px solid ${config.mode === 'development' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(194, 85, 92, 0.3)'}`,
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>
                  {config.mode === 'development' ? '🛠️' : '🚀'}
                </span>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Environment Mode</div>
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: config.mode === 'development' ? '#22c55e' : '#c2555c',
                textTransform: 'uppercase'
              }}>
                {config.mode}
              </div>
            </div>

            {/* Location Status */}
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>📍</span>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Location</div>
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#3b82f6',
                textTransform: 'uppercase'
              }}>
                {config.location}
              </div>
            </div>

            {/* Database Status */}
            <div style={{
              background: 'rgba(168, 85, 247, 0.1)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>💾</span>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Database</div>
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#a855f7'
              }}>
                PostgreSQL
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Connected ✓
              </div>
            </div>

            {/* Neo4j Status */}
            {config.neo4j && (
              <div style={{
                background: config.neo4j.connected
                  ? 'rgba(34, 197, 94, 0.1)'
                  : 'rgba(156, 163, 175, 0.1)',
                border: `1px solid ${config.neo4j.connected ? 'rgba(34, 197, 94, 0.3)' : 'rgba(156, 163, 175, 0.3)'}`,
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🔗</span>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Neo4j Graph DB</div>
                </div>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: config.neo4j.connected ? '#22c55e' : '#9ca3af'
                }}>
                  {config.neo4j.connected ? 'Connected' : 'Disconnected'}
                </div>
              </div>
            )}
          </div>

          {/* Mode Controls */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#3b82f6', marginBottom: '1rem', fontSize: '1.3rem' }}>
              🔄 Mode Controls
            </h3>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Switch between development and production environments. This affects logging, error handling, and feature availability.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => changeMode('development')}
                disabled={config.mode === 'development' || changing}
                style={{
                  padding: '1rem 2rem',
                  background: config.mode === 'development'
                    ? 'rgba(34, 197, 94, 0.2)'
                    : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  border: config.mode === 'development'
                    ? '1px solid rgba(34, 197, 94, 0.3)'
                    : 'none',
                  borderRadius: '8px',
                  color: config.mode === 'development' ? '#6b7280' : 'white',
                  fontWeight: 'bold',
                  cursor: config.mode === 'development' || changing ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: changing ? 0.5 : 1
                }}
              >
                🛠️ Switch to Development
              </button>
              <button
                onClick={() => changeMode('production')}
                disabled={config.mode === 'production' || changing}
                style={{
                  padding: '1rem 2rem',
                  background: config.mode === 'production'
                    ? 'rgba(194, 85, 92, 0.2)'
                    : 'linear-gradient(135deg, #c2555c 0%, #9c3f45 100%)',
                  border: config.mode === 'production'
                    ? '1px solid rgba(194, 85, 92, 0.3)'
                    : 'none',
                  borderRadius: '8px',
                  color: config.mode === 'production' ? '#6b7280' : 'white',
                  fontWeight: 'bold',
                  cursor: config.mode === 'production' || changing ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: changing ? 0.5 : 1
                }}
              >
                🚀 Switch to Production
              </button>
            </div>
          </div>

          {/* Location Controls */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#3b82f6', marginBottom: '1rem', fontSize: '1.3rem' }}>
              📍 Location Settings
            </h3>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Switch between development, closed beta, and production environments.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[
                { value: 'local', label: '💻 Local', desc: 'Development on local machine', color: '#3b82f6' },
                { value: 'dev_server', label: '🔒 Dev Server', desc: 'Closed Beta - Invite Only', color: '#f59e0b' },
                { value: 'server', label: '🌐 Server', desc: 'Production - Public Access', color: '#22c55e' }
              ].map(loc => (
                <button
                  key={loc.value}
                  onClick={() => changeLocation(loc.value)}
                  disabled={config.location === loc.value || changing}
                  style={{
                    padding: '1rem 1.5rem',
                    background: config.location === loc.value
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(255, 255, 255, 0.05)',
                    border: config.location === loc.value
                      ? '1px solid rgba(59, 130, 246, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: config.location === loc.value ? '#3b82f6' : 'white',
                    fontWeight: config.location === loc.value ? 'bold' : 'normal',
                    cursor: config.location === loc.value || changing ? 'not-allowed' : 'pointer',
                    opacity: changing ? 0.5 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '0.25rem'
                  }}
                >
                  <div style={{ fontSize: '1rem' }}>{loc.label}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 'normal' }}>
                    {loc.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Feature Flags */}
          {config.features && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '2rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#3b82f6', marginBottom: '1rem', fontSize: '1.3rem' }}>
                🚩 Active Features
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                {Object.entries(config.features).map(([key, enabled]) => (
                  <div
                    key={key}
                    style={{
                      padding: '1rem',
                      background: enabled
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(156, 163, 175, 0.1)',
                      border: `1px solid ${enabled ? 'rgba(34, 197, 94, 0.3)' : 'rgba(156, 163, 175, 0.3)'}`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span style={{ color: enabled ? '#22c55e' : '#9ca3af', fontSize: '0.9rem', fontWeight: '500' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span style={{ fontSize: '1.2rem' }}>
                      {enabled ? '✅' : '⭕'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* API Configuration */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '2rem'
          }}>
            <h3 style={{ color: '#3b82f6', marginBottom: '1rem', fontSize: '1.3rem' }}>
              🔌 Marketplace Integrations
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem'
            }}>
              {/* eBay */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                border: config.ebay?.enabled ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '1.5rem' }}>🛒</div>
                  <div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>eBay</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                      {config.ebay?.env || 'Sandbox'}
                    </div>
                  </div>
                </div>
                <div style={{ color: config.ebay?.enabled ? '#22c55e' : '#c2555c', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  {config.ebay?.enabled ? '✅ Active' : '❌ Inactive'}
                </div>
              </div>

              {/* Amazon - Placeholder */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                opacity: 0.6
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '1.5rem' }}>📦</div>
                  <div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>Amazon</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Coming Soon</div>
                  </div>
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  🔜 Not Configured
                </div>
              </div>

              {/* Etsy - Placeholder */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                opacity: 0.6
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '1.5rem' }}>🎨</div>
                  <div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>Etsy</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Coming Soon</div>
                  </div>
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  🔜 Not Configured
                </div>
              </div>

              {/* Walmart - Placeholder */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                opacity: 0.6
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '1.5rem' }}>🏪</div>
                  <div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>Walmart</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Coming Soon</div>
                  </div>
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  🔜 Not Configured
                </div>
              </div>
            </div>

            {/* Other APIs */}
            {config.apis && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '1rem' }}>
                  Other Services
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {Object.entries(config.apis).map(([key, value]) => (
                    <div
                      key={key}
                      style={{
                        padding: '1rem',
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div style={{ color: 'white', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {typeof value === 'boolean'
                          ? value ? '✅ Enabled' : '❌ Disabled'
                          : value || '(Not configured)'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Warning Note */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
              <div>
                <h4 style={{ color: '#eab308', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  Important Notes
                </h4>
                <div style={{ color: '#9ca3af', lineHeight: '1.6', fontSize: '0.95rem' }}>
                  <div>• Changing the mode or location will affect all users immediately</div>
                  <div>• Production mode has stricter error handling and logging</div>
                  <div>• Development mode enables additional debugging features</div>
                  <div>• Some changes may require a server restart to take full effect</div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
          No configuration data available
        </div>
      )}
    </div>
  );
};

export default AdminEnvironmentPage;
